import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Field, Icon, Input, Select } from "@sagot/ui";
import { useState } from "react";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import { ExitsEditor } from "../builder/exits-editor";
import type { FlowContext } from "../builder/flow-context";
import { useRefs } from "../builder/refs";
import type { Step } from "../types";
import { SERVICE_STEP_LIMITS } from "./service-constants";
import { typeOf } from "./service-model";
import type { FieldAnswers } from "./service-types";

const F = "engage.workflows.service.fields";

/** Where the ticket types, statuses and the rest are edited: the Service settings screen. */
export function SettingsLink({ label, tab }: { label: string; tab?: string }) {
  const { summary } = useWorkspaceContext();
  return <AppLink className="font-semibold text-primary-ink underline" href={consoleRoutes.serviceSettings(summary.slug, tab)}>{label}</AppLink>;
}

interface TypedStep {
  type_id?: string;
  type_key?: string;
}

interface TypeFieldProps {
  step: TypedStep;
  disabled: boolean;
  /** Label of the empty choice; unset when a type is required. */
  emptyLabel?: string;
  label: string;
  hint?: string;
  onChange: (next: { type_id?: string; type_key?: string }) => void;
}

/**
 * The ticket type a step names. The choice is stored by the type's id; a seeded type a default names by key stays selected
 * (and unchanged) until the person picks another. A type the workspace no longer has says so instead of showing a blank.
 */
export function TypeField({ step, disabled, emptyLabel, label, hint, onChange }: TypeFieldProps) {
  const { t } = useTranslation();
  const { service } = useRefs();
  const types = service?.types ?? [];
  const current = typeOf(step, service);
  const named = Boolean(step.type_id ?? step.type_key);
  const value = current?.id ?? (named ? "gone" : "");
  return (
    <div className="space-y-2">
      <Field label={label} hint={hint}>
        <Select
          value={value}
          disabled={disabled}
          onChange={(e) => {
            const id = e.target.value;
            onChange(id === "" || id === "gone" ? {} : { type_id: id });
          }}
        >
          {emptyLabel === undefined ? <option value="">{t(`${F}.choose`)}</option> : <option value="">{emptyLabel}</option>}
          {named && !current ? <option value="gone">{t(`${F}.typeGone`)}</option> : null}
          {types.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
        </Select>
      </Field>
      {types.length === 0 ? (
        <p className="text-xs text-fg-muted" data-testid="service-no-types">
          {t(`${F}.noTypes`)} <SettingsLink label={t(`${F}.openSettings`)} tab="types" />
        </p>
      ) : null}
    </div>
  );
}

interface ServiceExitsProps {
  step: Step;
  disabled: boolean;
  flow: Pick<FlowContext, "paths" | "canAddPath" | "facing">;
  onChange: (step: never) => void;
}

/**
 * What happens next for each outcome of a background Service Desk step. In a customer-facing workflow the step runs in the
 * visitor's turn and carries on with the next step, so an exit there would do nothing: the server refuses it, and the form says so.
 */
export function ServiceExits({ step, disabled, flow, onChange }: ServiceExitsProps) {
  const { t } = useTranslation();
  if (flow.facing) return <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted" data-testid="service-exits-facing">{t(`${F}.exitsFacing`)}</p>;
  return <ExitsEditor step={step} onChange={onChange as (step: Step) => void} paths={flow.paths} canAddPath={flow.canAddPath} disabled={disabled} />;
}

/** The ticket and contact variables a text of a Service Desk step can use (`{{ticket.number}}`). */
export const SERVICE_VARIABLES = ["ticket.number", "ticket.title", "ticket.link", "ticket.status", "ticket.priority", "organisation.name", "contact.name"] as const;

export function ServiceVariables() {
  const { t } = useTranslation();
  return (
    <div>
      <p className="mt-1.5 text-xs text-fg-subtle">
        {t(`${F}.variables`)}:{" "}
        {SERVICE_VARIABLES.map((name, i) => (
          <span key={name}>
            {i > 0 ? ", " : ""}
            <code className="font-mono">{`{{${name}}}`}</code>
          </span>
        ))}
      </p>
    </div>
  );
}

/** Splits typed tags on commas and applies the server limits (`SERVICE_STEP_LIMITS`). */
export function parseTags(raw: string): string[] {
  const typed = raw.split(",").map((tag) => tag.trim()).filter(Boolean);
  return [...new Set(typed.slice(0, SERVICE_STEP_LIMITS.tags).map((tag) => tag.slice(0, SERVICE_STEP_LIMITS.tag)))];
}

interface TagsFieldProps {
  label: string;
  hint: string;
  value: readonly string[];
  disabled: boolean;
  onChange: (tags: string[]) => void;
}

/** Tags as one comma-separated line, committed when the field loses focus (the same pattern as the tag steps). */
export function TagsField({ label, hint, value, disabled, onChange }: TagsFieldProps) {
  const [text, setText] = useState(value.join(", "));
  return (
    <Field label={label} hint={hint}>
      <Input
        value={text}
        disabled={disabled}
        onChange={(e) => { setText(e.target.value); }}
        onBlur={() => {
          const tags = parseTags(text);
          setText(tags.join(", "));
          onChange(tags);
        }}
      />
    </Field>
  );
}

interface AnswersFieldProps {
  value: FieldAnswers | undefined;
  disabled: boolean;
  onChange: (answers: FieldAnswers | undefined) => void;
}

/** Answers to the custom fields of the ticket, one row per field: the field and a template (`{{var.model}}`). */
export function AnswersField({ value, disabled, onChange }: AnswersFieldProps) {
  const { t } = useTranslation();
  const { service } = useRefs();
  const options = (service?.fields ?? []).filter((option) => (option.object ?? "ticket") === "ticket");
  const rows = Object.entries(value ?? {});
  const taken = new Set(rows.map(([key]) => key));
  const free = options.filter((option) => !taken.has(option.key));
  const commit = (next: [string, string][]) => { onChange(next.length > 0 ? Object.fromEntries(next) : undefined); };
  return (
    <fieldset className="space-y-2">
      <legend className="label">{t(`${F}.answers`)}</legend>
      {rows.map(([key, answer], index) => (
        <div key={key} className="flex items-end gap-2" data-testid="service-answer">
          <Field label={options.find((option) => option.key === key)?.name ?? key} className="flex-1">
            <Input maxLength={SERVICE_STEP_LIMITS.fieldTemplate} disabled={disabled} value={answer} onChange={(e) => { commit(rows.map((row, i): [string, string] => (i === index ? [key, e.target.value] : row))); }} />
          </Field>
          <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled} aria-label={t(`${F}.removeAnswer`, { name: options.find((option) => option.key === key)?.name ?? key })} onClick={() => { commit(rows.filter((_, i) => i !== index)); }}>
            <Icon name="trash" />
          </Button>
        </div>
      ))}
      {rows.length < SERVICE_STEP_LIMITS.fields && free.length > 0 ? (
        <Select aria-label={t(`${F}.addAnswer`)} className="h-9 w-auto text-xs" disabled={disabled} value="" onChange={(e) => { if (e.target.value) commit([...rows, [e.target.value, ""]]); }}>
          <option value="">{t(`${F}.addAnswer`)}</option>
          {free.map((option) => <option key={option.key} value={option.key}>{option.name}</option>)}
        </Select>
      ) : null}
      {options.length === 0 ? <p className="text-xs text-fg-muted">{t(`${F}.noFields`)}</p> : <p className="hint">{t(`${F}.answersHint`, { example: "{{var.model}}" })}</p>}
    </fieldset>
  );
}
